import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TAX_CONFIG, TOOL_PATHS, calcProfitToPocket, formatEUR, formatPct } from "@/config/tools";

export const parseAmount = (v) => Number(String(v).replace(/[^\d]/g, "")) || 0;

export function StatusToggle({ value, onChange, testid }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 border border-[var(--line-2)]" role="radiogroup" data-testid={testid}>
      {["non_dom", "domiciled"].map((s) => (
        <button
          key={s}
          type="button"
          role="radio"
          aria-checked={value === s}
          onClick={() => onChange(s)}
          data-testid={`${testid}-${s}`}
          className={`py-3 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${
            value === s ? "bg-[var(--bone)] text-[var(--ink)]" : "text-[var(--bone-2)] hover:text-[var(--bone)]"
          }`}
        >
          {t(`tools.status.${s}`)}
        </button>
      ))}
    </div>
  );
}

export default function ProfitTeaser() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [profit, setProfit] = useState(TAX_CONFIG.defaultProfit);
  const [status, setStatus] = useState("non_dom");
  const r = calcProfitToPocket({ profit, status });

  return (
    <div className="hero-teaser p-6 md:p-8" data-testid="hero-teaser">
      <div className="flex items-center gap-3 mb-6">
        <span className="live-dot" />
        <span className="overline">{t("tools.teaser.title")}</span>
      </div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mute)] mb-1" htmlFor="teaser-profit">
        {t("tools.teaser.profit")}
      </label>
      <input
        id="teaser-profit"
        inputMode="numeric"
        className="field !text-2xl !font-normal"
        value={profit ? new Intl.NumberFormat("en-US").format(profit) : ""}
        onChange={(e) => setProfit(parseAmount(e.target.value))}
        data-testid="teaser-profit-input"
        dir="ltr"
      />
      <div className="mt-6 mb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--mute)]">{t("tools.teaser.status")}</div>
      <StatusToggle value={status} onChange={setStatus} testid="teaser-status" />
      <div className="mt-7 pt-6 border-t border-[var(--line)]">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--pewter)]">{t("tools.teaser.result")}</div>
        <div className="display text-[var(--bone)] mt-2" style={{ fontSize: "clamp(40px, 4.4vw, 60px)", lineHeight: 1 }} data-testid="teaser-result">
          {formatEUR(r.pocket, lang)}
        </div>
        <p className="mt-3 text-xs text-[var(--mute)] leading-relaxed">
          {t("tools.teaser.note", { cit: formatPct(TAX_CONFIG.corporateTax, lang), sdc: formatPct(r.sdcRate, lang), gesy: formatPct(TAX_CONFIG.gesyDividend, lang, 2) })}
        </p>
        <Link
          to={`${TOOL_PATHS.profit}?profit=${profit}&status=${status}`}
          className="btn-secondary mt-6 w-full justify-center"
          data-testid="teaser-full-breakdown"
        >
          {t("tools.teaser.full")} <ArrowRight size={14} className="rtl:rotate-180" />
        </Link>
      </div>
    </div>
  );
}
